import { Trans } from "@lingui/macro";
import cx from "classnames";

import { USD_DECIMALS } from "config/factors";
import { getGlvMarketShortening, getGlvOrMarketAddress, getMarketPoolName } from "domain/synthetics/markets";
import { isGlvInfo } from "domain/synthetics/markets/glv";
import { GlvOrMarketInfo } from "domain/synthetics/markets/types";
import { useUserEarnings } from "domain/synthetics/markets/useUserEarnings";
import { TokenData, convertToUsd } from "domain/synthetics/tokens";
import { useChainId } from "lib/chains";
import { formatAmountHuman, formatBalanceAmount, formatUsd } from "lib/numbers";
import { getByKey } from "lib/objects";
import { usePoolsIsMobilePage } from "pages/Pools/usePoolsIsMobilePage";
import { getNormalizedTokenSymbol } from "sdk/configs/tokens";

import TokenIcon from "components/TokenIcon/TokenIcon";

import { PoolsDetailsMarketAmount } from "./PoolsDetailsMarketAmount";

type Props = {
  glvOrMarketInfo: GlvOrMarketInfo | undefined;
  marketToken: TokenData | undefined;
};

export function PoolsDetailsHeader({ glvOrMarketInfo, marketToken }: Props) {
  const { chainId } = useChainId();
  const isGlv = glvOrMarketInfo && isGlvInfo(glvOrMarketInfo);
  const iconName = glvOrMarketInfo?.isSpotOnly
    ? getNormalizedTokenSymbol(glvOrMarketInfo.longToken.symbol) +
      getNormalizedTokenSymbol(glvOrMarketInfo.shortToken.symbol)
    : isGlv
      ? glvOrMarketInfo?.glvToken.symbol
      : glvOrMarketInfo?.indexToken.symbol;

  const marketPrice = marketToken?.prices?.maxPrice;
  const marketBalance = marketToken?.balance;
  const marketBalanceUsd = convertToUsd(marketBalance, marketToken?.decimals, marketPrice);

  const marketTotalSupply = marketToken?.totalSupply;
  const marketTotalSupplyUsd = convertToUsd(marketTotalSupply, marketToken?.decimals, marketPrice);

  const userEarnings = useUserEarnings(chainId);
  const marketEarnings = getByKey(userEarnings?.byMarketAddress, marketToken?.address);

  const isMobile = usePoolsIsMobilePage();
  const poolName = glvOrMarketInfo ? getMarketPoolName(glvOrMarketInfo).replace(/\s*\/\s*/g, "/") : "";
  const primaryTitle = isGlv ? "NLV" : poolName;
  const secondaryTitle = isGlv ? poolName : undefined;

  return (
    <div
      className={cx("flex rounded-12 bg-gray-darker px-24 py-24", {
        "flex-col gap-10": isMobile,
        "items-center gap-36": !isMobile,
      })}
    >
      {glvOrMarketInfo ? (
        <>
          <div
            className={cx("flex items-center gap-20", {
              // "border-r": !isMobile,
              "pb-8": isMobile,
            })}
          >
            {iconName ? (
              <TokenIcon
                symbol={iconName}
                displaySize={32}
                importSize={40}
                badge={
                  isGlv
                    ? getGlvMarketShortening(chainId, getGlvOrMarketAddress(glvOrMarketInfo))
                    : ([glvOrMarketInfo.longToken.symbol, glvOrMarketInfo.shortToken.symbol] as const)
                }
              />
            ) : null}
            <div className={cx("flex flex-col gap-4 pr-20")}>
              <div className="text-body-large max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap font-medium text-white">
                {primaryTitle}
              </div>
              {secondaryTitle && (
                <div className="text-body-small max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap font-medium text-gray-lighter">
                  {secondaryTitle}
                </div>
              )}
            </div>
          </div>

          <PoolsDetailsMarketAmount
            label={<Trans>TVL</Trans>}
            value={formatAmountHuman(marketTotalSupplyUsd, USD_DECIMALS, true, 2)}
            secondaryValue={
              typeof marketTotalSupply === "bigint" && typeof marketToken?.decimals === "number"
                ? `${formatAmountHuman(marketTotalSupply, marketToken?.decimals, false, 2)} ${isGlv ? "NLV" : "NTM"}`
                : undefined
            }
          />

          {typeof marketBalance === "bigint" && typeof marketToken?.decimals === "number" ? (
            <PoolsDetailsMarketAmount
              label={<Trans>Wallet</Trans>}
              value={formatUsd(marketBalanceUsd)}
              secondaryValue={`${formatBalanceAmount(marketBalance, marketToken?.decimals, undefined, {
                showZero: true,
              })} ${isGlv ? "NLV" : "NTM"}`}
            />
          ) : null}

          {marketEarnings ? (
            <PoolsDetailsMarketAmount
              label={<Trans>Total Earned Fees</Trans>}
              value={formatUsd(marketEarnings?.total)}
            />
          ) : null}
        </>
      ) : (
        <div>...</div>
      )}
    </div>
  );
}
